import { useCallback } from 'react'

import { AuthRuntime } from '../runtime/AuthRuntime'
import { useAuthRuntime } from './AuthProvider'

export type FetchInput = RequestInfo | URL
export type FetchInit = RequestInit & {
    /**
     * Skip the auth header (useful for public endpoints from an
     * authenticated context). Default: false.
     */
    skipAuth?: boolean
}

export type AuthorizedFetch = (input: FetchInput, init?: FetchInit) => Promise<Response>

const isStatus401 = (response: Response): boolean => response.status === 401

/**
 * Builds a fetch wrapper that:
 * 1. Attaches `Authorization: Bearer <token>` from the runtime (calling
 *    `getAccessToken` which refreshes preemptively if near expiry).
 * 2. On a 401, runs single-flight refresh and retries the request once.
 *    A second 401 invalidates the session (terminal — `reauthRequired`).
 *
 * Used as the low-level building block for both the React Query plugin
 * and any ad-hoc fetch call.
 */
export const createAuthorizedFetch = (
    runtime: AuthRuntime,
    baseFetch: typeof fetch = fetch,
): AuthorizedFetch => {
    const attachAuth = async (init: FetchInit | undefined): Promise<RequestInit> => {
        const headers = new Headers(init?.headers ?? {})
        if (!init?.skipAuth) {
            const token = await runtime.getAccessToken()
            if (token) {
                headers.set('Authorization', `Bearer ${token}`)
            }
        }
        const next: RequestInit = { ...init, headers }
        delete (next as FetchInit).skipAuth
        return next
    }

    return async (input, init) => {
        const firstInit = await attachAuth(init)
        const firstResponse = await baseFetch(input, firstInit)
        if (!isStatus401(firstResponse) || init?.skipAuth) {
            return firstResponse
        }

        const refreshed = await runtime.refresh()
        if (!refreshed) {
            return firstResponse
        }

        const retryInit = await attachAuth(init)
        const retryResponse = await baseFetch(input, retryInit)
        if (isStatus401(retryResponse)) {
            await runtime.invalidate(new Error('401 after refresh — session invalidated'))
        }
        return retryResponse
    }
}

/** React hook variant — pulls the runtime from context. */
export const useAuthorizedFetch = (): AuthorizedFetch => {
    const runtime = useAuthRuntime()
    return useCallback<AuthorizedFetch>(
        (input, init) => createAuthorizedFetch(runtime)(input, init),
        [runtime],
    )
}
